import React from 'react';
import {Icon, Text} from '@albert-io/atomic';
import LicenseStatusCard from './LicenseStatusCard';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import type {ClassroomV1} from '@albert-io/models';

interface LicenseStatusRadioGroupProps extends PropsWithClassNameOptional {
  isUserForClassLicensed: boolean;
  isLicensed: boolean;
  setIsLicensed: (isLicensed: boolean) => void;
  disabled?: boolean;
  className?: string;
  classroom?: ClassroomV1;
}

const LicenseStatusRadioGroup = ({
  isUserForClassLicensed,
  isLicensed,
  setIsLicensed,
  disabled,
  className,
  classroom
}: LicenseStatusRadioGroupProps) => {
  const initialLicensedState = classroom?.apply_students_to_school_license;

  const {isFeatureEnabled: isUnlicensedClassroomsEnabled} =
    useIsFeatureFlagEnabled('unlicensed_classrooms');

  if (!isUnlicensedClassroomsEnabled) {
    return null;
  }

  const cantEnableLicense =
    !initialLicensedState && !classroom?.getMeta()?.isCanEnableLicense?.() && classroom;
  const cantDisableLicense =
    initialLicensedState && !classroom?.getMeta()?.isCanDisableLicense?.() && classroom;

  return (
    <div
      className={`flex flex-col gap-x1 ${className}`}
      role='radiogroup'
      aria-label='License status'
    >
      <Text size='m'>Class License Status</Text>
      {isUserForClassLicensed ? (
        <>
          <LicenseStatusCard
            isLicensed
            selected={isLicensed}
            onClick={() => setIsLicensed(true)}
            warning={
              cantEnableLicense
                ? 'Requires additional license seats. Reach out to your admin.'
                : undefined
            }
            disabled={disabled}
          />
          <LicenseStatusCard
            isLicensed={false}
            selected={!isLicensed}
            onClick={() => setIsLicensed(false)}
            warning={
              cantDisableLicense
                ? 'License access cannot be changed after a student places a guess.'
                : undefined
            }
            disabled={disabled}
          />
        </>
      ) : (
        <>
          <LicenseStatusCard isLicensed={initialLicensedState} readonly />
          <div className='flex items-center'>
            <Icon icon='info-circle' iconStyle='light' color='secondary' className='mr-x0-half' />
            <Text size='s' color='secondary' italic>
              Only licensed staff members can edit this setting.
            </Text>
          </div>
        </>
      )}
    </div>
  );
};

export default LicenseStatusRadioGroup;
